/**
 * @file Shared hook that resolves a pasted Transfermarkt player profile URL
 * to a player in our database and navigates to their profile (SE-3599).
 * @remarks
 * Used identically by every player-search surface (mobile Searchbar, web
 * HeaderSearchButton, dashboard PlayerSearch) so URL paste behaves the same
 * everywhere: extract the numeric Transfermarkt ID, look the player up via
 * `getPlayerByTransfermarktId`, auto-navigate on a hit, and expose a
 * not-found state for the surface's empty-state message on a miss.
 */

import { Href, usePathname, useRouter } from "expo-router";
import { useEffect, useMemo, useRef } from "react";

import { usePlayerSearchGetPlayerByTransfermarktIdLazyQuery } from "@/generated/graphql";
import { useGetExpandedPlayerUrl } from "@/hooks/useExpandedPlayerUrl";
import { useToastNotification } from "@/hooks/useToastNotification";
import {
  BreadcrumbEntry,
  useNavigationContext,
} from "@/providers/NavigationProvider";
import { parseTransfermarktId } from "@/utils/transfermarktUrl";

/**
 * Options for the Transfermarkt player lookup hook
 */
export interface TransfermarktLookupOptions {
  /**
   * Origin breadcrumb seeded before the player crumb.
   * Defaults to a "Search" crumb pointing at the current pathname with the
   * SE-5387 reopen-search flag, mirroring HeaderSearchButton's seeding.
   */
  origin?: BreadcrumbEntry;
  /**
   * Surface cleanup invoked after a successful navigation (close the popover,
   * clear the input, blur, …).
   */
  onNavigated?: () => void;
}

/**
 * State exposed by the Transfermarkt player lookup hook
 */
interface TransfermarktLookupResult {
  /** The input parses as a Transfermarkt player profile URL */
  isTransfermarktInput: boolean;
  /** The numeric Transfermarkt player ID extracted from the input, if any */
  transfermarktId: number | null;
  /** The lookup query is in flight */
  isLookingUp: boolean;
  /** Valid URL, lookup completed, and no player matched the ID */
  isNotFound: boolean;
}

/**
 * Resolves a pasted Transfermarkt player profile URL to a player profile.
 * @param text - The raw search input text
 * @param options - Breadcrumb origin and post-navigation cleanup
 * @returns Lookup state for the surface's loading / empty-state rendering
 */
export function useTransfermarktPlayerLookup(
  text: string,
  options?: TransfermarktLookupOptions
): TransfermarktLookupResult {
  const router = useRouter();
  const pathname = usePathname();
  const getExpandedUrl = useGetExpandedPlayerUrl();
  const { pushBreadcrumb, clearBreadcrumbs } = useNavigationContext();
  const toast = useToastNotification();

  const transfermarktId = useMemo(() => parseTransfermarktId(text), [text]);

  const [lookup, { data, loading, error, called, variables }] =
    usePlayerSearchGetPlayerByTransfermarktIdLazyQuery({
      fetchPolicy: "cache-and-network",
    });

  // Only trust results that belong to the ID currently in the input —
  // a stale response for a previous paste must not navigate.
  const player =
    transfermarktId !== null && variables?.transfermarktId === transfermarktId
      ? data?.getPlayerByTransfermarktId
      : undefined;

  // Callers pass options as inline literals; a ref keeps the navigation
  // effect from re-firing on every render over their identity.
  const optionsRef = useRef(options);

  // The navigated-player guard resets when the input stops being a
  // Transfermarkt URL (e.g. cleared after navigation) so re-pasting the same
  // URL later navigates again.
  const navigatedPlayerIdRef = useRef<string | null>(null);

  // Refs are synced in an effect (not during render) and this effect is
  // declared first, so later effects in the same pass see current values.
  useEffect(() => {
    optionsRef.current = options;
  });

  // Fire once per distinct ID. Pasting is a single change event; further
  // keystrokes after a complete URL re-parse to the same ID and must not
  // re-query.
  useEffect(() => {
    if (transfermarktId !== null) {
      lookup({ variables: { transfermarktId } });
    }
  }, [transfermarktId, lookup]);

  // Auto-navigate on a hit, once per resolved player.
  useEffect(() => {
    if (transfermarktId === null) {
      navigatedPlayerIdRef.current = null;
      return;
    }
    if (!player || navigatedPlayerIdRef.current === player.id) {
      return;
    }
    navigatedPlayerIdRef.current = player.id;

    const playerUrl = getExpandedUrl(`/players/${player.id}`);
    clearBreadcrumbs();
    pushBreadcrumb(
      optionsRef.current?.origin ?? {
        label: "Search",
        path: pathname,
        reopenSearch: true,
      }
    );
    pushBreadcrumb({
      label: player?.name || "Player",
      path: playerUrl,
    });
    optionsRef.current?.onNavigated?.();
    router.push(playerUrl as Href);
  }, [
    transfermarktId,
    player,
    getExpandedUrl,
    clearBreadcrumbs,
    pushBreadcrumb,
    pathname,
    router,
  ]);

  // A failed lookup is an error, not a "player not found" — surface it as a
  // transient toast and leave the not-found state untouched. Guarded by the
  // same variables?.transfermarktId === transfermarktId check as the other
  // stale-response guards above: without it, a still-stale error from a
  // previous ID can toast again while a newly-pasted ID is loading.
  useEffect(() => {
    if (
      error &&
      transfermarktId !== null &&
      variables?.transfermarktId === transfermarktId
    ) {
      toast.show({ action: "danger" });
    }
  }, [error, transfermarktId, variables, toast]);

  return {
    isTransfermarktInput: transfermarktId !== null,
    transfermarktId,
    isLookingUp: transfermarktId !== null && loading,
    isNotFound:
      transfermarktId !== null &&
      called &&
      !loading &&
      !error &&
      variables?.transfermarktId === transfermarktId &&
      data?.getPlayerByTransfermarktId === null,
  };
}
